import { AutoComplete, Listbox } from "../../styles/global.style";
import { ClickAwayListener, FormControl, InputAdornment, InputLabel, Typography, styled } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import React, { ChangeEvent, useEffect, useState } from "react";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";

import { Box } from "@mui/system";
import { IOptionGeneric } from "../../shared/interface";
import { InfoIcon } from "../Icons";
import { InputCustom } from "../../styles/styledComponents";
import { Loading } from "..";
import { paletteColors } from "../../styles/muiTheme";
import { useBreeds } from "../../services/breed";
import { useDebounce } from "../../hooks";

interface IBreedSearchAutocompleteProps {
  id?: string;
  name: string;
  control: Control<any>;
  specieId: string;
  text: string;
  onSelect?: (breed: IOptionGeneric) => void;
  onChange?: (text: string) => void;
  disabled?: boolean;
  placeholder?: string;
  tooltip?: string;
  breedWarning?: boolean;
  className?: string;
}

const BreedSearchAutocomplete = ({
  id,
  name,
  control,
  specieId,
  text,
  onSelect,
  onChange,
  placeholder,
  disabled,
  tooltip,
  breedWarning,
  className,
}: IBreedSearchAutocompleteProps) => {
  const [showPrediction, setShowPrediction] = useState(false);
  const [search, setSearch] = useState<string>("");
  const debouncedValue = useDebounce<string>(search);
  const { searchBreed, loading, breeds } = useBreeds();
  const [open, setOpen] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(event.target.value);
    setSearch(event.target.value);
  };

  const handleSelectPlace = (breed: any) => {
    setSearch(breed.label);
    onSelect && onSelect(breed);
    setShowPrediction(false);
  };

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: paletteColors.sand.main,
      color: paletteColors.black.main,
      fontSize: "12px",
      fontWeight: 400,
      lineHeight: "16px",
      fontFamily: "geograph-regular",
      padding: ".5rem 1rem",
      border: `1px solid ${paletteColors.taupe.dark}`,
      top: ".5rem",
      left: "-1.5rem",
      position: "absolute",
      width: "265px",
      p: {
        fontSize: "12px",
      },
    },
  });

  useEffect(() => {
    setSearch(text);
  }, [text]);

  useEffect(() => {
    searchBreed(specieId, debouncedValue);
  }, [debouncedValue]);

  return (
    <AutoComplete>
      <FormControl fullWidth={true}>
        <InputLabel htmlFor={id} sx={{ display: "flex", alignItems: "center" }} className={`${className}`}>
          Breed

          {tooltip && (
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <HtmlTooltip
                title={
                  <Typography color="inherit">{tooltip}</Typography>
                }
                placement="right"
                open={open}
              >
                <Box
                  sx={{ display: "grid", ml: .5 }}
                  onMouseEnter={() => setOpen(true)}
                  onMouseLeave={() => setOpen(false)}
                  onTouchStart={() => setOpen(true)}
                >
                  <InfoIcon width={18} height={18} />
                </Box>
              </HtmlTooltip>
            </ClickAwayListener>
          )}
        </InputLabel>
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState }) =>
            <InputCustom
              id={id}
              type="text"
              placeholder={placeholder ? placeholder : "Enter breed"}
              onFocus={() => {
                setShowPrediction(true);
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">
                  {loading && <Loading size={26} />}
                </InputAdornment>,
              }}
              disabled={disabled}
              autoComplete="off"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              {...field}
              value={search}
              onChange={handleChange}
            />
          }
        />

        {showPrediction && breeds?.length > 0 ? (
          <Listbox>
            {breeds.map((breed) => (
              <li key={breed.value} onClick={() => handleSelectPlace(breed)}>{breed.label}</li>
            ))}
          </Listbox>
        ) : null}

        {breedWarning && (
          <Box sx={{
            borderRadius: "4px",
            background: "#F9EED5",
            padding: "10px 16px",
            position: "relative",
            top: "8px",
            fontFamily: "geograph-regular",
            fontSize: 14,
            color: "#262626",
            mt: "8px",
            mb: "20px",
          }}>
              Choose &quot;other&quot; for unlisted breeds or &quot;unknown&quot; if unsure.
          </Box>
        )}
      </FormControl>
    </AutoComplete>
  );
};

export default BreedSearchAutocomplete;
